import { EnvironmentOnboardingConversation } from "@/components/environments/onboarding/EnvironmentOnboardingConversation";
import { ResizableHandle, ResizablePanel } from "@/components/flexkit/ResizablePanel";
import { ResizablePanelGroup } from "@/components/flexkit/ResizablePanel";
import { cn } from "@/components/podkit/lib/cn";
import { MainContent } from "@/components/SidebarLayout";
import { Flags } from "@/feature-flags";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useFlag } from "@/hooks/use-flag";
import { useStreamEvents } from "@/hooks/use-stream-events";
import { Agents, useAgentExecutionsByEnvironment } from "@/queries/agent-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { Suspense, useEffect, type FC } from "react";
import { Outlet, useParams } from "react-router-dom";

export const EnvironmentDetailsLayout: FC = () => {
    useDocumentTitle("Environment");

    const { environmentId } = useParams();
    const { unsubscribe } = useStreamEvents({ case: "environmentId", value: environmentId || "" });
    const { data: environment, isPending: isEnvironmentPending } = useEnvironment(environmentId);
    const { data: agentExecution, isPending: isAgentExecutionPending } = useAgentExecutionsByEnvironment({
        environmentId,
        agentId: Agents.DevelopmentEnvironmentSetup.id,
    });
    const { value: isOnaEnvironmentOnboardingEnabled } = useFlag(Flags.OnaEnvironmentOnboardingEnabled, false);

    const isPending = isEnvironmentPending || isAgentExecutionPending;

    const createdFromProject = environment?.metadata?.projectId;
    const showOnboardingConversation = !isPending && (!createdFromProject || agentExecution !== undefined);

    const onboardingConversationSize = showOnboardingConversation
        ? { defaultSize: 40, minSize: 30, maxSize: 70 }
        : { defaultSize: 0, minSize: 0, maxSize: 0 };

    useEffect(() => {
        return () => {
            unsubscribe();
        };
        // this should happen on unmount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isOnaEnvironmentOnboardingEnabled && environmentId) {
        return (
            <ResizablePanelGroup direction="horizontal" className="flex">
                <ResizablePanel
                    defaultSize={100 - onboardingConversationSize.defaultSize}
                    order={1}
                    id="environment-details"
                >
                    <MainContent>
                        <Suspense>
                            <Outlet />
                        </Suspense>
                    </MainContent>
                </ResizablePanel>
                <ResizableHandle
                    disabled={!showOnboardingConversation}
                    className={cn({ "my-2 ml-0.5 mr-[1px]": showOnboardingConversation })}
                />
                <ResizablePanel
                    defaultSize={onboardingConversationSize.defaultSize}
                    minSize={onboardingConversationSize.minSize}
                    maxSize={onboardingConversationSize.maxSize}
                    order={2}
                    id="onboarding-conversation"
                >
                    <EnvironmentOnboardingConversation key={environmentId} environmentId={environmentId} />
                </ResizablePanel>
            </ResizablePanelGroup>
        );
    }

    return (
        <MainContent>
            <Suspense>
                <Outlet />
            </Suspense>
        </MainContent>
    );
};
